// AreaOpsPinOverlay — pin layer drawn over the Area Ops MODAL image.
//
// Glued to a provided image element (anchorRef → the modal's <img>) via a
// per-frame rAF re-sync, so pins/rectangles track the image as the window
// resizes. Two jobs:
//   1. Render existing pins — a numbered badge with a status ring (queued /
//      running / done / failed); region pins also draw their rectangle.
//      A staged pin ("Listeye Ekle") looks like any other pin and is the one
//      clickable layer: a click reopens its popup (edit / apply / remove).
//   2. Capture the "add pin" gesture when a draw feature is active:
//        - region kind → drag a rectangle → normalized {x,y,width,height}
//        - point  kind → single click     → normalized {x,y}
//      Both normalized 0-1 relative to the rendered image rect. Drag is tracked
//      on window so a release outside the image still completes.

import { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import { LuCheck, LuX, LuLoaderCircle } from "react-icons/lu";

const RING = {
  draft: "#94a3b8",
  staged: "#94a3b8",
  queued: "#94a3b8",
  running: "#a963f5",
  done: "#22c55e",
  failed: "#ef4444",
};

// Draft box resize handles: [id, x, y] as fractions of the box, and their cursors.
const HANDLES = [
  ["nw", 0, 0],
  ["n", 0.5, 0],
  ["ne", 1, 0],
  ["e", 1, 0.5],
  ["se", 1, 1],
  ["s", 0.5, 1],
  ["sw", 0, 1],
  ["w", 0, 0.5],
];
const HANDLE_CURSOR = {
  nw: "nwse-resize",
  se: "nwse-resize",
  ne: "nesw-resize",
  sw: "nesw-resize",
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
};
const MIN_SIDE = 0.01;

// Move the edges a handle drags by (dx, dy) — normalized — keeping the box
// inside the image and at least MIN_SIDE wide / tall.
export function resizeRegion(r, handle, dx, dy) {
  let x0 = r.x;
  let y0 = r.y;
  let x1 = r.x + r.width;
  let y1 = r.y + r.height;
  if (handle.includes("w")) x0 = Math.min(Math.max(0, x0 + dx), x1 - MIN_SIDE);
  if (handle.includes("e")) x1 = Math.max(Math.min(1, x1 + dx), x0 + MIN_SIDE);
  if (handle.includes("n")) y0 = Math.min(Math.max(0, y0 + dy), y1 - MIN_SIDE);
  if (handle.includes("s")) y1 = Math.max(Math.min(1, y1 + dy), y0 + MIN_SIDE);
  const f = (v) => +v.toFixed(4);
  return { x: f(x0), y: f(y0), width: f(x1 - x0), height: f(y1 - y0) };
}

export default function AreaOpsPinOverlay({
  anchorRef,
  pins,
  drawFeature,
  onArea,
  onCancelDraw,
  renderDraftPopup,
  onPinClick,
  onResizeDraft,
}) {
  const getRect = useCallback(
    () => (anchorRef && anchorRef.current ? anchorRef.current.getBoundingClientRect() : null),
    [anchorRef]
  );

  const [rect, setRect] = useState(() => getRect());
  const [drag, setDrag] = useState(null); // { x0, y0, x1, y1 } viewport px
  const rectRef = useRef(rect);
  const dragStartRef = useRef(null);

  // Once a pin's job is DONE, its selection rectangle/badge lingers on the image.
  // Fade it out ~2.5s after completion (the version stays in the history list).
  //
  // Pins ALREADY "done" at mount were completed in a PREVIOUS session (restored
  // from localStorage) — their squares must NOT reappear on reopen. Seed them
  // into hidden + scheduled so they stay hidden; only pins that finish DURING
  // this session get the brief show-then-hide.
  const [hidden, setHidden] = useState(() => {
    const s = new Set();
    (pins || []).forEach((p) => {
      if (p.status === "done") s.add(p.id);
    });
    return s;
  });
  const scheduledRef = useRef(null);
  if (scheduledRef.current === null) scheduledRef.current = new Set(hidden);
  const timersRef = useRef([]);

  useEffect(() => {
    (pins || []).forEach((p) => {
      if (p.status === "done" && !scheduledRef.current.has(p.id)) {
        scheduledRef.current.add(p.id);
        const t = setTimeout(() => {
          setHidden((prev) => {
            const next = new Set(prev);
            next.add(p.id);
            return next;
          });
        }, 5000);
        timersRef.current.push(t);
      }
    });
  }, [pins]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  useEffect(() => {
    rectRef.current = rect;
  }, [rect]);

  useLayoutEffect(() => {
    setRect(getRect());
  }, [getRect]);

  // Re-measure every frame so pins track resize/scroll; only setState on change
  // (the overlay stays mounted the whole time the modal is open).
  useEffect(() => {
    let raf;
    const sync = () => {
      const r = getRect();
      // Keep the last good rect while the <img> is briefly zero-sized (e.g. the
      // result image swaps in after an op) so the capture layer doesn't vanish
      // mid-flow — otherwise the next region draw has nothing to hit.
      if (r && r.width > 0 && r.height > 0) {
        setRect((prev) => {
          if (
            prev &&
            prev.left === r.left &&
            prev.top === r.top &&
            prev.width === r.width &&
            prev.height === r.height
          ) {
            return prev;
          }
          return r;
        });
      }
      raf = requestAnimationFrame(sync);
    };
    raf = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(raf);
  }, [getRect]);

  const norm = useCallback((clientX, clientY) => {
    const r = rectRef.current;
    if (!r || !r.width || !r.height) return { x: 0, y: 0 };
    return {
      x: Math.min(1, Math.max(0, (clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (clientY - r.top) / r.height)),
    };
  }, []);

  const isRegion = drawFeature?.kind === "region";

  const handleWindowMove = useCallback((e) => {
    const s = dragStartRef.current;
    if (!s) return;
    setDrag({ x0: s.x0, y0: s.y0, x1: e.clientX, y1: e.clientY });
  }, []);

  const handleWindowUp = useCallback(
    (e) => {
      window.removeEventListener("mousemove", handleWindowMove);
      window.removeEventListener("mouseup", handleWindowUp);
      const s = dragStartRef.current;
      dragStartRef.current = null;
      setDrag(null);
      if (!s) return;
      const left = Math.min(s.x0, e.clientX);
      const top = Math.min(s.y0, e.clientY);
      const right = Math.max(s.x0, e.clientX);
      const bottom = Math.max(s.y0, e.clientY);
      if (right - left < 6 || bottom - top < 6) return; // accidental click
      const a = norm(left, top);
      const b = norm(right, bottom);
      const region = {
        x: +a.x.toFixed(4),
        y: +a.y.toFixed(4),
        width: +(b.x - a.x).toFixed(4),
        height: +(b.y - a.y).toFixed(4),
      };
      if (region.width <= 0 || region.height <= 0) return;
      onArea?.({ region });
    },
    [handleWindowMove, norm, onArea]
  );

  const onMouseDown = useCallback(
    (e) => {
      if (!isRegion) return;
      e.preventDefault();
      e.stopPropagation();
      dragStartRef.current = { x0: e.clientX, y0: e.clientY };
      setDrag({ x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY });
      window.addEventListener("mousemove", handleWindowMove);
      window.addEventListener("mouseup", handleWindowUp);
    },
    [isRegion, handleWindowMove, handleWindowUp]
  );

  const onClickPoint = useCallback(
    (e) => {
      if (isRegion) return;
      e.preventDefault();
      e.stopPropagation();
      const p = norm(e.clientX, e.clientY);
      onArea?.({ point: { x: +p.x.toFixed(4), y: +p.y.toFixed(4) } });
    },
    [isRegion, norm, onArea]
  );

  // Esc cancels an active draw (and tears down any in-progress drag).
  useEffect(() => {
    if (!drawFeature) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        window.removeEventListener("mousemove", handleWindowMove);
        window.removeEventListener("mouseup", handleWindowUp);
        dragStartRef.current = null;
        setDrag(null);
        onCancelDraw?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawFeature, onCancelDraw, handleWindowMove, handleWindowUp]);

  // Drop window listeners on unmount.
  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleWindowMove);
      window.removeEventListener("mouseup", handleWindowUp);
    };
  }, [handleWindowMove, handleWindowUp]);

  if (!rect) return null;

  const dragBox =
    drag && isRegion
      ? {
          left: Math.min(drag.x0, drag.x1),
          top: Math.min(drag.y0, drag.y1),
          width: Math.abs(drag.x1 - drag.x0),
          height: Math.abs(drag.y1 - drag.y0),
        }
      : null;

  const statusIcon = (status) => {
    if (status === "running")
      return <LuLoaderCircle size={11} style={{ animation: "spin 1s linear infinite" }} />;
    if (status === "done") return <LuCheck size={11} />;
    if (status === "failed") return <LuX size={11} />;
    return null;
  };

  // Flatten pins into drawable layers, all in the single-pin look (status ring
  // + number):
  //   batch pin → one layer per op (its region; spinner until the job ends,
  //               then that op's own ✓/✗)
  //   staged    → like a draft, red if its last batch run failed; clickable
  //   other     → as is
  // Numbers are positions, not the stored pin.num (that one keeps growing with
  // every draft, cancelled ones too): open pins and ops read 1..n in order, and
  // a finished pin shows only its ✓/✗ until it fades.
  // A whole-image pin (no region) is drawn as a dashed frame around the photo.
  const WHOLE_REGION = { x: 0, y: 0, width: 1, height: 1 };
  let seq = 0;
  const numberIf = (open) => (open ? ++seq : "");
  const layers = (pins || []).flatMap((p) => {
    if (hidden.has(p.id)) return [];
    // A staged whole-image step has no box: it is listed in the history column.
    if (p.status === "staged" && p.whole) return [];
    const open = p.status !== "done";
    if (Array.isArray(p.batchOps)) {
      const settling = p.status === "queued" || p.status === "running";
      return p.batchOps
        .filter((op) => op.region)
        .map((op) => {
          const status = settling ? "running" : p.status === "failed" ? "failed" : op.status;
          return {
            key: `${p.id}-${op.opId}`,
            region: op.region,
            label: numberIf(open),
            status,
          };
        });
    }
    const staged = p.status === "staged";
    return [
      {
        key: p.id,
        region: p.whole ? WHOLE_REGION : p.region,
        whole: !!p.whole,
        point: p.point,
        label: numberIf(open),
        status: staged && p.error ? "failed" : p.status,
        onClick: staged && onPinClick ? () => onPinClick(p.id) : undefined,
        resizable: p.status === "draft" && !!p.region && !!onResizeDraft,
        pinId: p.id,
      },
    ];
  });

  // Drag a draft box's edge / corner: live-resizes it through onResizeDraft.
  const startResize = (pinId, handle, start) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const x = e.clientX;
    const y = e.clientY;
    const move = (ev) => {
      const r = rectRef.current;
      if (!r || !r.width || !r.height) return;
      onResizeDraft?.(
        pinId,
        resizeRegion(start, handle, (ev.clientX - x) / r.width, (ev.clientY - y) / r.height)
      );
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  return createPortal(
    <>
      {/* Pins layer — display only; just staged pins take a click (edit). */}
      <div
        className="area-ops-pins"
        style={{
          position: "fixed",
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
          pointerEvents: "none",
          zIndex: 3000075,
        }}
      >
        {layers.map((l) => {
          const ring = RING[l.status] || RING.queued;
          const px = l.region ? l.region.x : l.point ? l.point.x : 0.5;
          const py = l.region ? l.region.y : l.point ? l.point.y : 0.5;
          // Staged pins only: the click target that reopens the popup.
          const click = l.onClick
            ? { onClick: l.onClick, title: i18n.t("Edit") }
            : { onClick: undefined, title: undefined };
          const clickStyle = l.onClick ? { pointerEvents: "auto", cursor: "pointer" } : null;
          return (
            <div key={l.key}>
              {l.region && (
                <div
                  className="area-ops-pin-rect"
                  {...click}
                  style={{
                    position: "absolute",
                    left: `${l.region.x * 100}%`,
                    top: `${l.region.y * 100}%`,
                    width: `${l.region.width * 100}%`,
                    height: `${l.region.height * 100}%`,
                    border: `2px ${l.whole ? "dashed" : "solid"} ${ring}`,
                    background: l.whole ? "transparent" : `${ring}22`,
                    borderRadius: 4,
                    boxSizing: "border-box",
                    ...clickStyle,
                  }}
                >
                  {l.resizable &&
                    HANDLES.map(([id, hx, hy]) => (
                      <div
                        key={id}
                        className="area-ops-pin-handle"
                        data-handle={id}
                        onMouseDown={startResize(l.pinId, id, l.region)}
                        style={{
                          position: "absolute",
                          left: `${hx * 100}%`,
                          top: `${hy * 100}%`,
                          width: 10,
                          height: 10,
                          transform: "translate(-50%, -50%)",
                          background: "#fff",
                          border: `2px solid ${ring}`,
                          borderRadius: 2,
                          boxSizing: "border-box",
                          cursor: HANDLE_CURSOR[id],
                          pointerEvents: "auto",
                        }}
                      />
                    ))}
                </div>
              )}
              <div
                className="area-ops-pin-badge"
                data-status={l.status}
                {...click}
                style={{
                  position: "absolute",
                  left: `${px * 100}%`,
                  top: `${py * 100}%`,
                  transform: "translate(-50%, -50%)",
                  minWidth: 22,
                  height: 22,
                  padding: "0 5px",
                  borderRadius: 999,
                  background: "#fff",
                  border: `2px solid ${ring}`,
                  color: ring,
                  ...clickStyle,
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                }}
              >
                {l.label !== "" && <span>{l.label}</span>}
                {statusIcon(l.status)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Capture layer — only while adding a pin. */}
      {drawFeature && (
        <div
          className="area-ops-capture"
          onMouseDown={onMouseDown}
          onClick={onClickPoint}
          style={{
            position: "fixed",
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            cursor: "crosshair",
            zIndex: 3000076,
            background: "rgba(169,99,245,0.08)",
            outline: "2px dashed rgba(169,99,245,0.8)",
            outlineOffset: -2,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 8,
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(15,23,42,0.82)",
              color: "#fff",
              padding: "5px 12px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: "nowrap",
              pointerEvents: "none",
            }}
          >
            {isRegion ? i18n.t("AreaOps.DrawRegionHint") : i18n.t("AreaOps.ClickPointHint")}
          </div>
        </div>
      )}

      {/* Live drag rectangle */}
      {dragBox && dragBox.width > 0 && dragBox.height > 0 && (
        <div
          style={{
            position: "fixed",
            left: dragBox.left,
            top: dragBox.top,
            width: dragBox.width,
            height: dragBox.height,
            border: "2px solid #a963f5",
            background: "rgba(169,99,245,0.18)",
            pointerEvents: "none",
            zIndex: 3000077,
          }}
        />
      )}

      {/* Draft config popups — anchored ON the image to the SIDE of each draft's
          region (right if it fits, else left) so they never fall below the image
          or off-screen. Inputs are entered on the photo, not in the history. */}
      {renderDraftPopup &&
        (pins || [])
          .filter((p) => p.status === "draft")
          .map((p) => {
            const POPUP_W = 272;
            const GAP = 12;
            const rLeft =
              rect.left + (p.region ? p.region.x : p.point ? p.point.x : 0.5) * rect.width;
            const rRight =
              rect.left +
              (p.region ? p.region.x + p.region.width : p.point ? p.point.x : 0.5) * rect.width;
            const rTop =
              rect.top + (p.region ? p.region.y : p.point ? p.point.y : 0.5) * rect.height;

            // Prefer right of the region; flip to the left if it would overflow.
            // A whole-image draft has no side to sit on: centred at the top.
            let left = rRight + GAP;
            if (p.whole) left = rect.left + (rect.width - POPUP_W) / 2;
            else if (left + POPUP_W > window.innerWidth - 8) left = rLeft - POPUP_W - GAP;
            left = Math.max(8, Math.min(left, window.innerWidth - POPUP_W - 8));
            // Keep it fully on-screen vertically (content scrolls inside if tall).
            const top = Math.max(
              8,
              Math.min(p.whole ? rect.top + GAP : rTop, window.innerHeight - 360)
            );

            return (
              <div
                key={`${p.id}-popup`}
                className="aops-popup"
                style={{
                  position: "fixed",
                  left,
                  top,
                  zIndex: 3000080,
                  pointerEvents: "auto",
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {renderDraftPopup(p)}
              </div>
            );
          })}
    </>,
    document.body
  );
}
